import RichEditorWithSuggestions from "components/ResumeForm/Form/RichEditorWithSuggestions";
import { Input } from "components/ResumeForm/Form/InputGroup";
import { ProjectsForm } from "components/ResumeForm/ProjectsForm";
import { CreateHandleChangeArgsWithDescriptions } from "components/ResumeForm/types";
import { useAppSelector } from "lib/redux/hooks";
import { changeProjects, selectProjects } from "lib/redux/resumeSlice";
import { ResumeProject } from "lib/redux/types";
import { calculateWordCount } from "lib/resume-scoring/content-details-sections/word-count";
import {
  IndividualProjectsAnalysis,
  ResumeAnalysis,
} from "lib/resume-scoring/resume-analysis/resume-analyse-types";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { getDateIssue } from "score-check/utils/getDateIssue";
import { NoIssueBox } from "../common/NoIssueBox";
import { ShortDescription } from "../common/ShortDescription";
import { PassiveVoice } from "../common/PassiveVoice";
import { LongSentences } from "../common/LongSentences";
import { DoesNotEndsWithPeriod } from "../common/DoesNotEndsWithPeriod";
import { PersonalPronouns } from "../common/PersonalPronouns";
import { SectionHeader } from "../common/SectionHeader";
import { MistakesSection } from "../common/MistakesSection";
import { WordMistakesSection } from "../common/WordMistakesSection";
import { FixButtons } from "../common/FixButtons";
import { DateInputSection } from "../common/DateInputSection";
import { TextInputSection } from "../common/TextInputSection";
import { AddMoreSection } from "../common/AddMoreSection";
import { ImpactfulStatementWarning } from "../common/ImpactfulStatementWarning";
import { ReverseOrderWarning } from "../common/ReverseOrderWarning";
import { checkReverseOrder } from "score-check/utils/checkReverseOrder";
import { ProjectScoreAnalysis } from "lib/resume-scoring/types";
import Image from "next/image";
import { MistakesDisplayV2 } from "../common/MistakesDisplayV2";
import { highlightSection, clearHighlight } from "lib/redux/highlightSlice";
import { GrammarMistakesSection } from "../common/GrammarMistakes";

export const ProjectsSection = ({
  score,
  maxScore,
  analysis,
}: {
  score: ProjectScoreAnalysis;
  maxScore: number;
  analysis: ResumeAnalysis["projects"];
}) => {
  const [isAddProjectOpen, setIsAddProjectOpen] = useState(false);
  const projects = useAppSelector(selectProjects);

  // Check if projects are in reverse chronological order
  const projectsInReverseOrder = !checkReverseOrder(
    analysis.individualProjectsAnalysis
  );

  return (
    <div className="scrollbar-hide relative mb-4 flex h-full max-h-[88.5vh] flex-col rounded-[12px] bg-white p-1 shadow-md lg:rounded-[6px]">
      {/* Fixed Sticky Header */}
      <SectionHeader
        title="Projects"
        score={score.totalScore}
        maxScore={maxScore}
      />

      {/* Scrollable Content - Hidden Scrollbar */}
      <div className="scrollbar-hide flex flex-1 flex-col gap-[25px] overflow-auto px-0 pb-3 lg:gap-[30px] lg:px-0 xl:gap-[40px]">
        {analysis.individualProjectsAnalysis.map((project, index) => (
          <IndividualProject
            key={index}
            project={project}
            index={index}
            projects={projects}
          />
        ))}
        {projectsInReverseOrder && (
          <ReverseOrderWarning sectionName="Project" />
        )}

        <AddMoreSection
          isOpen={isAddProjectOpen}
          toggleOpen={() => setIsAddProjectOpen(!isAddProjectOpen)}
          label="Projects"
        />
        {isAddProjectOpen && <ProjectsForm /> }
      </div>

      {/* Add custom scrollbar hide styles */}
      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

const IndividualProject = ({
  project,
  index,
  projects,
}: {
  project: IndividualProjectsAnalysis;
  index: number;
  projects: ResumeProject[];
}) => {
  const isDateIssue = !project.startDate || !project.endDate;
  const isProjectNameMissing =
    !project.project || project.project.trim() === "";
  const hasGrammarMistakes = project.descriptionAnalysis.some(
    (desc) => desc.analysis.grammarMistakes.length > 0
  );
  const hasSpellingMistakes = project.descriptionAnalysis.some(
    (desc) => desc.analysis.spellingMistakes.length > 0
  );
  const isLinkMissing =
    !project.projectLink || project.projectLink.trim() === "";

  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false);
  const dispatch = useDispatch();
  const handleProjectChange = (
    ...[field, value]: CreateHandleChangeArgsWithDescriptions<ResumeProject>
  ) => {
    dispatch(changeProjects({ idx: index, field, value } as any));
  };

  // Get an array of non-past tense verbs that are the first word of a sentence
  // Deduplicate to show each unique verb only once
  const actionVerbIssues = [
    ...new Set(
      project.descriptionAnalysis.flatMap((desc) => {
        return desc.analysis.actionVerbAnalysis
          .filter((verb) => {
            // Check if the verb is not in past tense
            if (verb.isPastTense !== 0) return false;

            // Get the verb position in the statement
            const statement = desc.statement.trim();
            const firstWord = statement
              .split(" ")[0]
              .toLowerCase()
              .replace(/[^\w]/g, "");

            // Only include if it's the first word of the sentence
            return firstWord === verb.verb.toLowerCase();
          })
          .map((verb) => verb.verb);
      })
    ),
  ];

  const hasActionVerbIssue = actionVerbIssues.length > 0;
  // const hasBuzzWordsIssue = project.descriptionAnalysis.some(
  //   (desc) => desc.analysis.buzzWords.length > 0
  // );
  // const hasFillerWordsIssue = project.descriptionAnalysis.some(
  //   (desc) => desc.analysis.fillerWords.length > 0
  // );
  const hasPassiveVoiceIssue = project.descriptionAnalysis.some(
    (desc) => desc.analysis.isPassiveVoice == 1
  );

  const hasImpactifulStatement = project.descriptionAnalysis.some((desc) => {
    return desc.analysis.isQuantified == 1;
  });

  const getHasDescriptionIssues = (): boolean => {
    let has = false;
    project.descriptionAnalysis.map((desc) => {
      if (hasActionVerbIssue) has = true;
      if (desc.analysis.buzzWords.length > 0) has = true;
      if (desc.analysis.fillerWords.length > 0) has = true;
      if (desc.analysis.grammarMistakes.length > 0) has = true;
      if (desc.analysis.spellingMistakes.length > 0) has = true;
      if (desc.analysis.isPassiveVoice == 1) has = true;
      if (desc.analysis.personalPronouns.length > 0) has = true;
      // if (desc.analysis.endsWithPeriod === 0) has = true;
    });
    // if (project.descriptionAnalysis.length < 2) has = true;
    // if (!hasImpactifulStatement) has = true;
    return has;
  };

  // const doesNotEndsWithPeriod = project.descriptionAnalysis
  //   .filter((desc) => desc.analysis.endsWithPeriod == 0)
  //   .map((desc) => desc.statement);

  // Deduplicate personal pronouns - show each unique pronoun only once
  // const personalPronouns = [
  //   ...new Set(
  //     project.descriptionAnalysis
  //       .filter((desc) => desc.analysis.personalPronouns.length > 0)
  //       .map((desc) => desc.analysis.personalPronouns)
  //       .flat()
  //   )
  // ];

  const doesNotEndsWithPeriod = [
    ...new Set(
      project.descriptionAnalysis
        .filter((desc) => desc.analysis.endsWithPeriod == 0)
        .map((desc) => desc.statement)
    ),
  ];

  // Deduplicate personal pronouns - show each unique pronoun only once
  const personalPronouns = [
    ...new Set(
      project.descriptionAnalysis
        .filter((desc) => desc.analysis.personalPronouns.length > 0)
        .map((desc) => desc.analysis.personalPronouns)
        .flat()
    ),
  ];

  const longSentences = project.descriptionAnalysis
    .filter((desc) => calculateWordCount(desc.statement) > 25)
    .map((desc) => desc.statement);
  const hasStatementIssue = getHasDescriptionIssues();

  const isShortDescription = project.descriptionAnalysis.length < 2;

  const dateIssueText = getDateIssue(project.startDate, project.endDate);

  // Section-level highlighting handlers
  const handleSectionMouseEnter = () => {
    dispatch(
      highlightSection({
        section: "projects",
        itemIndex: index,
      })
    );
  };

  const handleSectionMouseLeave = () => {
    dispatch(clearHighlight());
  };

  const localRepeatedActionVerbs = (() => {
    const verbs: string[] = project.descriptionAnalysis.flatMap((d) =>
      d.analysis.actionVerbAnalysis.map((v) => v.verb)
    );
    const counts: Record<string, number> = {};
    verbs.forEach((v) => {
      const key = (v || "").trim().toLowerCase();
      if (!key) return;
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.keys(counts)
      .filter((k) => counts[k] > 1)
      .map((k) => k);
  })();

  // Local repeated phrases (simple trigram-based detection across this experience statements)
  const localRepeatedPhrases = (() => {
    const shingles: string[] = [];
    const normalize = (s: string) =>
      s
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, " ")
        .replace(/\s+/g, " ");
    project.descriptionAnalysis.forEach((d) => {
      const text = normalize(d.statement || "");
      if (!text) return;
      const words = text.split(" ").filter(Boolean);
      const n = 3; // trigrams
      for (let i = 0; i + n <= words.length; i++) {
        shingles.push(words.slice(i, i + n).join(" "));
      }
    });
    const counts: Record<string, number> = {};
    shingles.forEach((s) => (counts[s] = (counts[s] || 0) + 1));
    return Object.keys(counts).filter((k) => counts[k] > 1);
  })();

  // Filter out null/falsy values and deduplicate
  const spellingMistakes = [
    ...new Set(
      project.descriptionAnalysis
        .flatMap((point) =>
          point.analysis.spellingMistakes.map((mistake) => mistake.incorrect)
        )
        .filter(Boolean) as string[]
    ),
  ];

  const buzzWords = [
    ...new Set(
      project.descriptionAnalysis
        .flatMap((point) => point.analysis.buzzWords)
        .filter(Boolean) as string[]
    ),
  ];

  // Split into words and phrases
  const repeatedWords = localRepeatedActionVerbs; // Already deduplicated in the logic above
  const repeatedPhrases = localRepeatedPhrases; // Already deduplicated in the logic above

  // Grammar mistakes - filter out null/falsy values and deduplicate
  const grammarMistakes = [
    ...new Set(
      project.descriptionAnalysis
        .flatMap((point) =>
          point.analysis.grammarMistakes.map((m) => m.incorrect)
        )
        .filter(Boolean) as string[]
    ),
  ];

  const isMissingInfo = isDateIssue || isProjectNameMissing || isLinkMissing;
  const showFixButton =
    isMissingInfo ||
    hasStatementIssue ||
    !hasImpactifulStatement ||
    doesNotEndsWithPeriod.length > 0 ||
    isShortDescription ||
    longSentences.length > 0 ||
    doesNotEndsWithPeriod.length > 0;
    

  return (
    <div className="mt-0 flex flex-col gap-4 rounded-2xl bg-gradient-to-br from-white to-gray-50 p-4 pt-0 font-montserrat shadow-sm transition-shadow hover:shadow-md lg:gap-6 lg:p-2 lg:pt-0 xl:gap-8 xl:p-4 xl:pt-0">
      <span
        className="sticky top-0 z-10 mx-auto flex cursor-pointer items-center justify-center gap-3 rounded-xl bg-gray-100 px-[16px] py-[4px] text-center text-sm font-semibold text-black shadow-md transition-opacity hover:opacity-90 md:text-lg lg:px-[20px] lg:py-[8px] lg:text-xl xl:px-[24px]"
        onMouseEnter={handleSectionMouseEnter}
        onMouseLeave={handleSectionMouseLeave}
      >
        Project {index + 1}
      </span>

      {/* Missing Information*/}
      {isMissingInfo && (
        <div className="relative mt-3 flex flex-col gap-[10px] rounded-xl bg-[#FEE7E4] px-[15px] py-[10px] lg:gap-[16px] lg:px-[20px] lg:py-[14px] xl:mt-1">
          <div className="absolute left-4 top-[-16px] flex items-center gap-2 rounded-xl bg-white px-4 py-2 font-bold text-black shadow-md lg:left-8 lg:px-6">
            <Image
              src="/icons/score-check/missing.svg"
              height={24}
              width={24}
              alt="icon"
              className="h-4 w-4 lg:h-5 lg:w-5"
            />
            <span className="text-sm font-bold lg:text-base xl:text-lg">
              Missing Information
            </span>
          </div>

          <div className="mt-6">
            <div className="flex flex-col gap-2">
              {/* Project Name */}
              {isProjectNameMissing && (
                <TextInputSection
                  value={projects[index].project}
                  placeholder="Project Name*"
                  fieldName="project"
                  missingText="Project name missing"
                  handleChange={(field, value) =>
                    handleProjectChange(field, value)
                  }
                  icon="link"
                />
              )}

              {/* Date issue */}
              {isDateIssue && (
                <DateInputSection
                  startDate={projects[index].startDate}
                  endDate={projects[index].endDate}
                  dateIssueText={dateIssueText}
                  handleChange={(field, value) =>
                    handleProjectChange(field, value)
                  }
                />
              )}

              {/* Project Link */}
              {isLinkMissing && (
                <TextInputSection
                  value={projects[index].projectLink}
                  placeholder="https://something.com"
                  fieldName="projectLink"
                  missingText="Project link missing"
                  handleChange={(field, value) =>
                    handleProjectChange(field, value)
                  }
                  icon="link"
                />
              )}
            </div>
          </div>
          <div className="mt-4 flex flex-col items-center justify-center gap-3">
            <div className="relative h-32 w-32 lg:h-40 lg:w-40">
              <Image
                src="/assets/resumeScore/missingInfo.png"
                alt="Long sentences illustration"
                fill
                className="object-contain"
              />
            </div>
            <p className="text-center text-sm font-semibold text-red-600 lg:text-base">
              Missing information can weaken your resume
            </p>
          </div>
        </div>
      )}

      {/* Issues display */}
      {/* {hasStatementIssue && (
        <MistakesSection
          mistakes={project.descriptionAnalysis
            .filter((point) => {
              // Only include statements that have at least one issue
              return (
                point.analysis.grammarMistakes.length > 0 ||
                point.analysis.spellingMistakes.length > 0 ||
                point.analysis.buzzWords.length > 0 ||
                point.analysis.fillerWords.length > 0 ||
                point.analysis.isPassiveVoice === 1 ||
                point.analysis.personalPronouns.length > 0 ||
                (point.analysis.actionVerbAnalysis.length > 0 &&
                  point.analysis.actionVerbAnalysis.some(
                    (verb) => verb.isPastTense === 0
                  )) ||
                point.analysis.endsWithPeriod === 0 ||
                calculateWordCount(point.statement) > 25
              );
            })
            .map((point) => point.statement)}
        />
      )} */}

      {/* Spelling, Buzz Words, Repeated Words */}
      <MistakesDisplayV2
        spellingMistakes={spellingMistakes}
        buzzWords={buzzWords}
        repeatedWords={repeatedWords}
        section="projects"
        itemIndex={index}
      />

      {/* Grammar Section */}
      {hasGrammarMistakes && (
        <GrammarMistakesSection
          title="Grammar mistake"
          icon="grammarMistake"
          section="projects"
          itemIndex={index}
          mistakes={grammarMistakes}
        />
      )}

      {personalPronouns.length > 0 && (
        <PersonalPronouns
          personalPronouns={personalPronouns}
          section="projects"
          itemIndex={index}
        />
      )}

      {/* Repeated Phrases */}
      {/* {repeatedPhrases.length > 0 && (
        <WordMistakesSection
          title="Repeated phrases"
          icon="repeatedWords"
          section="projects"
          itemIndex={index}
          mistakeType="filler"
          mistakes={repeatedPhrases}
        />
      )} */}

      {/* Weak Verbs */}
      {hasActionVerbIssue && (
        <WordMistakesSection
          title="Past tense not used"
          icon="spellingMistake"
          section="projects"
          itemIndex={index}
          mistakeType="actionVerb"
          mistakes={actionVerbIssues}
        />
      )}

      {doesNotEndsWithPeriod.length > 0 && (
        <DoesNotEndsWithPeriod
          doesNotEndsWithPeriod={doesNotEndsWithPeriod}
          section="projects"
          itemIndex={index}
        />
      )}

      <LongSentences
        longSentences={longSentences}
        section="projects"
        itemIndex={index}
      />

      {hasPassiveVoiceIssue && <PassiveVoice />}

      {isShortDescription && <ShortDescription />}

      <ImpactfulStatementWarning
        hasImpactfulStatements={hasImpactifulStatement}
      />

      {/* Fix Buttons */}
      {showFixButton && (
        <>
          {isDescriptionOpen && (
            <div className="mx-auto flex w-full flex-col items-center justify-center gap-3 rounded-xl bg-white p-4 lg:p-6">
              {/* Project Name */}
              <Input
                label="Project Name"
                labelClassName="w-full"
                name="project"
                placeholder="Awesome App"
                value={projects[index].project}
                onChange={handleProjectChange}
              />

              {/* Start Date and End Date */}
              <div className="grid w-full grid-cols-2 gap-3">
                <Input
                  label="Start Date"
                  labelClassName="w-full"
                  name="startDate"
                  placeholder="Fall 2024"
                  value={projects[index].startDate}
                  onChange={handleProjectChange}
                />
                <Input
                  label="End Date"
                  labelClassName="w-full"
                  name="endDate"
                  placeholder="Fall 2024"
                  value={projects[index].endDate}
                  onChange={handleProjectChange}
                />
              </div>

              {/* Project Link */}
              <Input
                label="Project Link"
                labelClassName="w-full"
                name="projectLink"
                placeholder="https://yourproject.com"
                value={projects[index].projectLink}
                onChange={handleProjectChange}
              />

              {/* Description Editor */}
              <div className="w-full">
                <RichEditorWithSuggestions
                  name="descriptions"
                  value={projects[index].descriptions.map((desc) => desc)}
                  onChange={handleProjectChange}
                  placeholder="Project Description"
                  section="projects"
                />
              </div>
            </div>
          )}
          <FixButtons
            isEditMode={isDescriptionOpen}
            toggleEditMode={() => setIsDescriptionOpen(!isDescriptionOpen)}
          />
        </>
      )}

      {!showFixButton && (
        <NoIssueBox message="No issues found in this project description. Great job!" />
      )}
    </div>
  );
};
